import { useCallback, useEffect, useRef, useState } from 'react';

interface UsePanelResizeOptions {
  /** localStorage key the width persists under. */
  readonly storageKey: string;
  readonly minWidth: number;
  readonly maxWidth: number;
  readonly defaultWidth: number;
  /** Told when a drag starts/ends so the container can drop pointer-events on the page. */
  readonly onDragStateChange?: (dragging: boolean) => void;
  /**
   * Told the width SYNCHRONOUSLY, in the same event handler as the hook's own
   * setState (plus once on mount, for the initial value). Passing it here —
   * rather than watching `width` with an effect in the panel — matters for
   * drags: an effect fires a render AFTER the panel's own commit, so the
   * container applied the new width one frame late and every pointermove cost
   * two full render passes. In the same handler, React batches both setStates
   * into one.
   */
  readonly onWidthChange?: (width: number) => void;
}

interface UsePanelResizeResult {
  readonly width: number;
  readonly isDragging: boolean;
  /** Spread onto the divider element. */
  readonly handleProps: {
    readonly role: 'separator';
    readonly tabIndex: 0;
    readonly 'aria-orientation': 'vertical';
    readonly 'aria-valuenow': number;
    readonly 'aria-valuemin': number;
    readonly 'aria-valuemax': number;
    readonly onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
    readonly onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
    readonly onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
    readonly onKeyDown: (e: React.KeyboardEvent) => void;
    readonly onKeyUp: () => void;
  };
}

/**
 * Read a panel's persisted width: parsed, clamped, and with a hostile storage
 * treated as "nothing stored".
 *
 * Exported because a stored width has more than one reader. The panel that owns
 * the drag seeds its own state from it, and so does the CONTAINER that paints
 * the column and the page margin around that panel — and until they shared this
 * function they disagreed three ways:
 *
 *  - no clamp, so a width dragged under a narrower viewport's floor came back
 *    on desktop as a column painted at 300px around a panel rendering 420, and
 *    stayed wrong for the session rather than for a frame;
 *  - no NaN check, so a non-numeric stored value made the container's width
 *    state literally `NaN`;
 *  - no try/catch, so `localStorage` throwing (Safari with site data blocked)
 *    took down the whole tree from inside a layout hook.
 *
 * @param storageKey  localStorage key the width is persisted under.
 * @param minWidth    Floor of the drag range; a stored value below it clamps up.
 * @param maxWidth    Ceiling of the drag range; a stored value above it clamps down.
 * @param defaultWidth Used when nothing is stored, the value is not a number, or
 *                     storage is unavailable.
 */
export function readPanelWidth(
  storageKey: string,
  minWidth: number,
  maxWidth: number,
  defaultWidth: number,
): number {
  if (typeof window === 'undefined') return defaultWidth;
  try {
    const saved = window.localStorage.getItem(storageKey);
    if (saved === null) return defaultWidth;
    const parsed = Number.parseInt(saved, 10);
    if (Number.isNaN(parsed)) return defaultWidth;
    // Clamp on READ as well as write: a stored value from before a min/max
    // change (or a hand-edited one) must not resurrect an unusable width.
    return Math.max(minWidth, Math.min(parsed, maxWidth));
  } catch {
    // Storage disabled — fall through to the default, session-only.
    return defaultWidth;
  }
}

/**
 * Drag-to-resize for a left-docked panel, implementing the contract
 * `docs/architecture/frontend.md` § Chrome panels sets out. That contract was
 * written from two rounds of real bugs, so the details are not stylistic:
 *
 *  - **Pointer events + `setPointerCapture`**, never document-level mouse
 *    listeners: releasing outside the window never fires `mouseup` there, which
 *    stranded the drag until the next click.
 *  - **Delta-based** (`startWidth + (clientX - startX)`), never an absolute
 *    viewport coordinate — an absolute reading is wrong by whatever sits to the
 *    left of the panel, and silently drifts if that changes.
 *  - **Refs for the drag origin and the reached width**: the move handler must
 *    not be rebuilt every frame, and the persist-on-end must see the width the
 *    last move produced rather than the last committed render.
 *  - **The panel reports width upward; the CONTAINER applies it.** A panel that
 *    also sizes itself picks up the new width a render before its container and
 *    overhangs the page mid-drag.
 *  - Keyboard-operable, because the handle is focusable and a trackpad drag is
 *    not everyone's option.
 *
 * `BirdChatPanel` and `SplitViewContainer` each still carry their own copy of
 * the DRAG; they should adopt this hook, but migrating two verified panels is
 * its own change. The stored-width READ is already shared — see
 * {@link readPanelWidth}.
 */
export function usePanelResize({
  storageKey,
  minWidth,
  maxWidth,
  defaultWidth,
  onDragStateChange,
  onWidthChange,
}: UsePanelResizeOptions): UsePanelResizeResult {
  const [width, setWidth] = useState<number>(() =>
    readPanelWidth(storageKey, minWidth, maxWidth, defaultWidth),
  );
  const [isDragging, setIsDragging] = useState(false);

  const clamp = useCallback(
    (candidate: number) => Math.max(minWidth, Math.min(candidate, maxWidth)),
    [minWidth, maxWidth],
  );

  const dragOriginRef = useRef<{ pointerX: number; width: number } | null>(null);
  const reachedWidthRef = useRef(width);

  // Ref'd so the handlers below never close over a stale callback without
  // having to rebuild per render.
  const onWidthChangeRef = useRef(onWidthChange);
  onWidthChangeRef.current = onWidthChange;

  /** The one place a width change happens: state, the drag-end ref, and the
   *  container's synchronous notification all move together. */
  const applyWidth = useCallback((next: number) => {
    reachedWidthRef.current = next;
    setWidth(next);
    onWidthChangeRef.current?.(next);
  }, []);

  // Report the initial (possibly restored-from-storage) width once on mount so
  // the container starts in sync without the panels needing their own effect.
  useEffect(() => {
    onWidthChangeRef.current?.(reachedWidthRef.current);
  }, []);

  const persist = useCallback(() => {
    try {
      window.localStorage.setItem(storageKey, String(reachedWidthRef.current));
    } catch {
      // Quota or disabled storage — the width still applies for this session.
    }
  }, [storageKey]);

  const endDrag = useCallback(() => {
    // Idempotent: the handle's own pointerup and the window safety net below can
    // both fire, and double-ending would emit a spurious drag-stopped.
    if (!dragOriginRef.current) return;
    dragOriginRef.current = null;
    setIsDragging(false);
    onDragStateChange?.(false);
    persist();
  }, [onDragStateChange, persist]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      dragOriginRef.current = { pointerX: e.clientX, width };
      reachedWidthRef.current = width;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      setIsDragging(true);
      onDragStateChange?.(true);
    },
    [width, onDragStateChange],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const origin = dragOriginRef.current;
      if (!origin) return;
      applyWidth(clamp(origin.width + (e.clientX - origin.pointerX)));
    },
    [clamp, applyWidth],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (!dragOriginRef.current) return;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      endDrag();
    },
    [endDrag],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const step = e.shiftKey ? 64 : 16;
      // Computed outside the updater: a state updater must stay pure, since
      // StrictMode double-invokes it, and notifying/persisting are side effects.
      applyWidth(clamp(width + (e.key === 'ArrowLeft' ? -step : step)));
      // Persisted on key UP: auto-repeat fires ~30 keydowns a second, and a
      // synchronous localStorage write per event is what the drag path avoids.
    },
    [clamp, width, applyWidth],
  );

  // Safety net for the handle unmounting mid-drag — pointer capture dies with
  // the element, so its own pointerup never arrives and the drag would stick.
  useEffect(() => {
    if (!isDragging) return;
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    return () => {
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
    };
  }, [isDragging, endDrag]);

  // Re-clamp if the bounds move under us (a viewport resize narrowing maxWidth).
  // Read from the ref and apply outside a state updater — updaters must stay
  // pure (StrictMode re-invokes them), and applyWidth notifies the container.
  useEffect(() => {
    const next = Math.max(minWidth, Math.min(reachedWidthRef.current, maxWidth));
    if (next !== reachedWidthRef.current) applyWidth(next);
  }, [minWidth, maxWidth, applyWidth]);

  return {
    width,
    isDragging,
    handleProps: {
      role: 'separator',
      tabIndex: 0,
      'aria-orientation': 'vertical',
      'aria-valuenow': width,
      'aria-valuemin': minWidth,
      'aria-valuemax': maxWidth,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onKeyDown,
      onKeyUp: persist,
    },
  };
}
